
import type {
  BaseErrorResponse,
  EmptySuccessResponse,
  ExportDocumentsReq,
  GetDocumentTypesRes,
  GetDocumentsRes,
  ExportHeadersResponse,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useDocumentRegistry() {
  class DocumentRegistry<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Document registry
     * @name DocumentRegistryControllerGetDocuments
     * @summary Get documents
     * @request GET:/api3/document-registry/document
     * @secure
     * @response `200` `GetDocumentsRes`
     * @response `400` `BaseErrorResponse`
     */
    documentRegistryControllerGetDocuments = (
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example [{"field":"document-state.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"document-state.created","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"document-state.updated","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"document-state.guid","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"document-state.accountId","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"document-state.documentType","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"document-state.is_batch","operation":"{ != | = }","value":null},{"field":"document-state.success","operation":"{ != | = }","value":null},{"field":"document-state.period_id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null}] */
        filter?: object;
        /**
         * Sort options
         * @example {"document-state.id":"{ ASC | DESC }","document-state.created":"{ ASC | DESC }","document-state.updated":"{ ASC | DESC }","document-state.guid":"{ ASC | DESC }","document-state.accountId":"{ ASC | DESC }","document-state.documentType":"{ ASC | DESC }","document-state.is_batch":"{ ASC | DESC }","document-state.success":"{ ASC | DESC }","document-state.period_id":"{ ASC | DESC }"}
         */
        orderBy?: object;
        /** @example "Purchase" */
        documentType?: string;
        /** @example 1 */
        accountId?: number;
        /** @example true */
        isBatch?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetDocumentsRes, BaseErrorResponse>({
        path: '/api3/document-registry/document',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document registry
     * @name DocumentRegistryControllerExportDocuments
     * @summary Start export
     * @request POST:/api3/document-registry/document/export
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    documentRegistryControllerExportDocuments = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example "Purchase" */
        documentType?: string;
        /** @example 1 */
        accountId?: number;
        /** @example true */
        isBatch?: boolean;
        /** @example [] */
        filter?: object;
      },
      data: ExportDocumentsReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/document-registry/document/export',
        method: 'POST',
        query,
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document registry
     * @name DocumentRegistryControllerGetExportHeaders
     * @summary Get export headers
     * @request GET:/api3/document-registry/document/export/headers
     * @secure
     * @response `200` `ExportHeadersResponse`
     * @response `400` `BaseErrorResponse`
     */
    documentRegistryControllerGetExportHeaders = (params: RequestParams = {}) =>
      this.request<ExportHeadersResponse, BaseErrorResponse>({
        path: '/api3/document-registry/document/export/headers',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Document registry
     * @name DocumentRegistryControllerGetDocumentTypes
     * @summary Get document types
     * @request GET:/api3/document-registry/document-type
     * @secure
     * @response `200` `GetDocumentTypesRes`
     * @response `400` `BaseErrorResponse`
     */
    documentRegistryControllerGetDocumentTypes = (params: RequestParams = {}) =>
      this.request<GetDocumentTypesRes, BaseErrorResponse>({
        path: '/api3/document-registry/document-type',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new DocumentRegistry();
}
